// The single source of client-specific configuration for this demo.
//
// This is the templatization seam: to skin the same layered "break-it"
// demo for another vertical/prospect, copy this file and change the
// strings + layer metadata here. Components read from SCENARIO and never
// hard-code client names, amounts, or narrative text of their own.

import type { BreakKind } from "../break.js";

export interface AgentStep {
  id: string;
  label: string;
  detail: string;
}

export interface ScenarioLayer {
  // Stable id used for selection state and keys.
  id: string;
  // Short badge, e.g. "L1", "L3", "L3!".
  badge: string;
  title: string;
  // What this layer adds on top of the previous one.
  blurb: string;
  // Structured teaching copy: 2-4 short sentences (one per array entry)
  // saying what this layer adds and, concretely, what an outside party can
  // now verify that they could not at the layer below.
  explainer: string[];
  // The demo-artifacts folder name to load + verify. null for L0 (no receipt).
  layerDataId: string | null;
  // Which client-side breaks apply to this layer.
  breaks: BreakKind[];
}

// Copy for a collapsible artifact view (schema / template / receipt).
export interface ArtifactCopy {
  title: string;
  explainer: string[];
}

// The vendor invoice that arrives in the accounts-payable inbox — the thing the
// agent ingests. Showing it as the starting point makes the automation
// concrete: no human keys this in; the agent reads the document itself.
export interface IncomingInvoice {
  title: string;
  inbox: string;
  receivedAt: string;
  vendor: string;
  invoiceId: string;
  poNumber: string;
  issuedAt: string;
  lineItems: { description: string; qty: number; unitUsd: number }[];
  totalLabel: string;
  note: string;
  explainer: string[];
}

// One step in the Overview process strip: what actually happened, from the
// invoice arriving to anyone verifying the receipt.
export interface FlowStep {
  // Icon key, resolved to an inline SVG in ProcessFlow.
  icon: "invoice" | "inbox" | "agent" | "seal" | "verify";
  label: string;
  sub: string;
  // The steps Sequesign adds (sealed receipt + verify) get the tinted treatment.
  seq?: boolean;
  // The verify step tints its icon with the "pass" color.
  verify?: boolean;
}

// One side-by-side receipt card on the landing hero. Both cards verify a
// recorded package in-browser; they sit at the same trust level (witnessed +
// policy-bound) and differ only in the conformance verdict, which is the whole
// point of showing them together.
export interface HeroCard {
  // The recorded demo-artifacts folder (also the layerDataId) this card loads
  // and verifies live.
  bundleId: string;
  // Short status chip, e.g. "Within authority" / "Over authority".
  tag: string;
  amount: string;
  headline: string;
  line: string;
  // One line describing what the visitor will see once they verify.
  expect: string;
}

export interface Scenario {
  client: { name: string; domain: string };
  product: { name: string; tagline: string };
  disclosure: string;
  // The default landing hero: a two-sentence hook, two side-by-side verifiable
  // receipt cards (conformant + over-authority), and the CTAs into the deeper
  // flows. Kept here so the whole demo stays reskinnable from this one file.
  hero: {
    eyebrow: string;
    headline: string;
    sub: string;
    verifyLabel: string;
    verifyingLabel: string;
    reverifyLabel: string;
    browserNote: string;
    cards: { conformant: HeroCard; violation: HeroCard };
    layersCta: { label: string; sub: string };
    liveCta: { label: string; sub: string };
  };
  // The Overview process strip: invoice → inbox → agent → sealed receipt →
  // verify. The seq steps are the layer Sequesign adds.
  flow: { seqLabel: string; steps: FlowStep[] };
  incomingInvoice: IncomingInvoice;
  task: {
    title: string;
    prompt: string;
    authority: string;
    conformantOutcome: string;
    violationOutcome: string;
    agentId: string;
    delegator: string;
    invoiceId: string;
  };
  agentSteps: AgentStep[];
  agentDecision: string;
  layers: ScenarioLayer[];
  // Client/narrative copy for the L0 raw-output reveal and the collapsible
  // artifact views. Kept here (not in components) so the whole demo can be
  // reskinned by editing this one file.
  copy: {
    rawOutput: ArtifactCopy;
    schemaView: ArtifactCopy;
    templateView: ArtifactCopy;
    receiptView: ArtifactCopy;
    // The seal-time refusal demonstration (task E).
    schemaRefusal: {
      title: string;
      buttonLabel: string;
      explainer: string[];
      // Shown once the refusal fires, to make clear no receipt exists.
      noReceiptNote: string;
    };
    // Intro line above the "break it" controls; accurate for all break kinds.
    breaksIntro: string;
  };
  // Labels and hints for each client-side "break it" control.
  breaks: Record<BreakKind, { label: string; hint: string }>;
  // Copy for Live mode: a real end-to-end run against the real witness +
  // Anthropic with the presenter's own keys, verified in EXTERNAL trust-anchor
  // mode. Kept here so the whole demo stays reskinnable from this one file.
  live: {
    title: string;
    intro: string[];
    // Prominent note that live mode contacts real services with the
    // presenter's keys, and that keys are used only for the run.
    contactNote: string;
    keyNote: string;
    runLabel: string;
    runningLabel: string;
    // Labels for the credential + input fields.
    fields: {
      agentPem: string;
      agentId: string;
      witnessBaseUrl: string;
      witnessApiKey: string;
      llmApiKey: string;
      amountUsd: string;
      vendor: string;
      invoiceId: string;
    };
  };
}

export const SCENARIO: Scenario = {
  client: {
    name: "Torbridge Supply Co.",
    domain: "torbridgesupply.com"
  },
  product: {
    name: "Sequesign",
    tagline: "Verifiable receipts for agent actions"
  },
  disclosure:
    "Fictional demonstration. Torbridge Supply Co. is not a real company. This page demonstrates Sequesign receipt verification; verification is performed live in your browser.",
  hero: {
    eyebrow: "See it in 15 seconds",
    headline: "An AI agent approved these payments. Trust it, or verify it?",
    sub: "Two invoices arrived in Torbridge's accounts-payable inbox, and its AI agent handled both (no one keyed in the data) under a standing $5,000 auto-pay authority. One it approved for $1,850, within the limit; the other, a larger order, for $12,400, over it. Each run is sealed into a receipt you recompute yourself, right here in your browser. Check them:",
    verifyLabel: "Verify this receipt",
    verifyingLabel: "Verifying…",
    reverifyLabel: "Verify again",
    browserNote:
      "Each check is recomputed with the real Sequesign verifier, right here in your browser. Nothing is sent anywhere.",
    cards: {
      conformant: {
        bundleId: "L3-template-conformant",
        tag: "Within authority",
        amount: "$1,850",
        headline: "Approved $1,850",
        line: "Inside the standing $5,000 auto-pay authority. The agent created the payment instruction and sealed the receipt.",
        expect:
          "You should expect every signature, hash, and the bound mandate to check out, and the work to conform to the rules."
      },
      violation: {
        bundleId: "L3-template-violation",
        tag: "Over authority",
        amount: "$12,400",
        headline: "Approved $12,400",
        line: "A second, larger invoice ($12,400 from Cascade Instrument Supply), over the $5,000 limit. The agent approved it anyway, and sealed the receipt.",
        expect:
          "The receipt is just as valid, but instead of hiding the breach, it records it honestly as conformant: false."
      }
    },
    layersCta: {
      label: "See each trust layer, and try to break one",
      sub: "Step from raw agent output up to a countersigned receipt, then tamper with a sealed value and watch the verifier catch it."
    },
    liveCta: {
      label: "Run a real seal with your own keys",
      sub: "Presenter mode: run a live Sequesign session against the real witness and seal a brand-new receipt end to end."
    }
  },
  flow: {
    seqLabel: "The Sequesign layer",
    steps: [
      { icon: "invoice", label: "Invoice arrives", sub: "A vendor invoice lands as an email" },
      { icon: "inbox", label: "AP inbox", sub: "accounts-payable@torbridge" },
      {
        icon: "agent",
        label: "AI agent",
        sub: "Reads it, checks policy, decides. No data entry"
      },
      {
        icon: "seal",
        label: "Sealed receipt",
        sub: "The action is cryptographically sealed",
        seq: true
      },
      {
        icon: "verify",
        label: "Anyone verifies",
        sub: "Checked offline, in any browser",
        seq: true,
        verify: true
      }
    ]
  },
  incomingInvoice: {
    title: "An invoice arrived",
    inbox: "accounts-payable@torbridge.example",
    receivedAt: "2026-06-15 08:42",
    // Vendor + amount must match the recipient and amount sealed into the
    // recorded receipt (payment_instruction_created → "Summit Medical Supply
    // Co.", $1,850), so the invoice a visitor sees is the same one the agent
    // actually handled.
    vendor: "Summit Medical Supply Co.",
    invoiceId: "INV-2026-0847",
    poNumber: "PO-77103",
    issuedAt: "2026-06-14",
    lineItems: [
      { description: "Nitrile exam gloves, size M (case of 1000)", qty: 3, unitUsd: 240 },
      { description: "Isolation gowns, level 2 (case of 100)", qty: 2, unitUsd: 415 },
      { description: "Freight & handling", qty: 1, unitUsd: 300 }
    ],
    totalLabel: "$1,850.00",
    note: "This landed in the AP inbox as an email attachment. The agent picked it up, read the fields off the document, and handled it. No one keyed the data into a form.",
    explainer: [
      "This is where the work starts: a vendor invoice arrives in the accounts-payable inbox.",
      "No one opens it and types the vendor, amount, and PO number into a system. The agent reads the document itself, extracts the fields, and takes it from there.",
      "Everything below is the agent handling this invoice. The trust layers after it are about proving what it did."
    ]
  },
  task: {
    title: "Accounts-payable agent: vendor invoice review",
    prompt:
      "Review vendor invoice INV-2026-0847 against the purchase order and the accounts-payable policy. If it clears the checks and falls within the standing auto-pay authority, create the payment instruction; otherwise route it for human approval.",
    authority: "Standing auto-pay authority: up to $5,000 per invoice.",
    conformantOutcome: "The conformant run pays $1,850, inside the $5,000 authority.",
    violationOutcome:
      "A separate, larger invoice ($12,400 from Cascade Instrument Supply) is over the $5,000 authority; the same agent approved it anyway and sealed the receipt honestly with conformant: false.",
    agentId: "torbridge-ap-agent-01",
    delegator: "d.alvarez@torbridge.example",
    invoiceId: "INV-2026-0847"
  },
  agentSteps: [
    {
      id: "task_created",
      label: "task_created",
      detail: "Received: review + approve vendor invoice INV-2026-0847 ($1,850)."
    },
    {
      id: "policy_checked",
      label: "policy_checked",
      detail:
        "AP policy ap-v1: auto-pay limit $5,000. Invoice $1,850. PO match confirmed. Decision: approved."
    },
    {
      id: "llm_invoice_reviewed",
      label: "llm_invoice_reviewed",
      detail: "Line items and totals reviewed against the purchase order. No discrepancies found."
    },
    {
      id: "payment_instruction_created",
      label: "payment_instruction_created",
      detail: "Payment instruction created for $1,850 to the vendor."
    }
  ],
  agentDecision:
    "APPROVED: pay $1,850 for invoice INV-2026-0847. Within the $5,000 auto-pay authority; purchase-order match confirmed.",
  layers: [
    {
      id: "L0",
      badge: "Raw",
      title: "Raw agent output",
      blurb:
        "Just the agent's transcript and decision. Nothing here can be independently checked. You either trust the screenshot or you don't.",
      explainer: [
        "This is the starting point: an agent produced some text and a decision.",
        "There is no signature, no hash chain, and no witness: nothing an outside party can check.",
        "You either trust the screenshot or you don't. Every layer below adds one concrete thing a third party can independently verify."
      ],
      layerDataId: null,
      breaks: []
    },
    {
      id: "L1",
      badge: "Sealed",
      title: "Sealed receipt (freeform)",
      blurb:
        "The same run, now sealed: a hash-linked action chain, each step signed by the agent and countersigned by a witness. Tamper with any recorded byte and verification fails.",
      explainer: [
        "Sealing turns the transcript into a sealed receipt: a hash-linked chain of the recorded actions, each step signed by the agent and countersigned by a witness.",
        "An outside party can check that the recorded bytes have not changed since sealing and that the agent and witness keys really signed them. In this recorded demo the witness key is the one bundled in the receipt, an integrity self-check; anchoring instead to the witness's published keys (what the live version does) is what makes it an independent third-party check. The trust-anchor line in the panel shows which mode you are in.",
        "That is strictly more than the raw output gave you: the content of the run is now tamper-evident, not just asserted. Try the break below to see a single edited character get caught."
      ],
      layerDataId: "L1-freeform",
      breaks: ["tamper-evidence"]
    },
    {
      id: "L2",
      badge: "Schema",
      title: "Schema-validated",
      blurb:
        "Adds declared evidence schemas. Every evidence blob must match its schema, so the recorded fields are not just intact: they are the right shape and values.",
      explainer: [
        "This step adds a declared JSON Schema for every evidence blob, pinned into the receipt by its hash.",
        "Beyond the previous step's tamper-evidence, an outside party can now check that each blob is the right shape and holds valid values (the decision is one of the allowed values and the amount is a number) and, for the schemas that set additionalProperties:false, that no unexpected fields were smuggled in.",
        "Because the schema is committed by hash, the rules themselves cannot be swapped out later without the verifier noticing. Both breaks below make that concrete."
      ],
      layerDataId: "L2-schema",
      breaks: ["tamper-evidence", "tamper-schema"]
    },
    {
      id: "L3",
      badge: "Mandate",
      title: "Template-constrained (conformant)",
      blurb:
        "Binds the run to a workflow mandate with a $5,000 payment cap. This run stayed within it. The receipt is cryptographically bound to the mandate and conforms to its rules.",
      explainer: [
        "This step binds the run to a workflow mandate (a template with allowed actions and, here, a $5,000 payment cap) whose hash is committed into the chain's genesis, so every action is bound to the same rules.",
        "On top of the schema checks, an outside party can now verify which mandate governed the run and that the recorded work actually conformed to it.",
        "This run paid $1,850, inside the cap, so conformance passes. Open the template below to see the exact rules the run was checked against."
      ],
      layerDataId: "L3-template-conformant",
      breaks: ["tamper-evidence"]
    },
    {
      id: "L3!",
      badge: "Violation",
      title: "Template-constrained (VIOLATION)",
      blurb:
        "A second, larger invoice: the agent approved $12,400, over the $5,000 cap. The receipt is still valid and bound to the mandate. It honestly records that the work did NOT conform. Verifiable proof of a violation, not a hidden one.",
      explainer: [
        "Same mandate as the conformant run, but a separate, larger invoice: the agent approved $12,400, over the $5,000 cap.",
        "The receipt is still structurally valid and bound to the same mandate, every signature and hash checks out, but conformance now fails.",
        "That is the point: an outside party gets verifiable proof that a violation happened, recorded honestly, rather than a violation quietly hidden or a receipt that simply refuses to exist."
      ],
      layerDataId: "L3-template-violation",
      breaks: []
    },
    {
      id: "L5",
      badge: "Countersigned",
      title: "Countersigned",
      blurb:
        "Adds a human countersignature (D. Alvarez) to the conformant run, an approval attestation bound into the receipt on top of everything below.",
      explainer: [
        "This step adds a human countersignature to the conformant run: an approval attestation cryptographically bound into the sealed receipt, on top of the agent and witness signatures, surfaced as the Approval badge, not as a higher level.",
        "An outside party can verify that an approver key countersigned this exact run and that the approval is part of the tamper-evident chain, an accountable step beyond the agent signing its own work.",
        "What the signature alone does not establish is whose key it is: in this recorded demo the approver's identity is self-asserted (the panel labels it so). Tying it to the named human, D. Alvarez, takes a trusted identity registration, which is the difference between verifying a signature and verifying an identity."
      ],
      layerDataId: "L5-countersigned",
      breaks: []
    }
  ],
  copy: {
    rawOutput: {
      title: "Raw agent output",
      explainer: [
        "This JSON is the agent's actual output, the payment instruction it produced.",
        "Without a receipt, this is all you get: an unverifiable transcript that anyone could have edited after the fact.",
        "There is no proof a policy was followed, no proof a witness ever saw it, and no way to bind it to a mandate. Everything in the layers below adds something an outside party can independently check."
      ]
    },
    schemaView: {
      title: "The evidence schemas",
      explainer: [
        "These are the JSON Schemas each evidence blob in this run is validated against.",
        "Schema validation means the evidence isn't just intact. It is the right shape and values: the decision is one of the allowed values and amounts are numbers. A schema can also forbid unexpected fields: most of these set additionalProperties:false, while llm_invoice_reviewed deliberately leaves it open, and the schemas below show exactly which.",
        'Each schema is pinned by hash in the receipt, so the rules cannot be swapped out later without the verifier detecting it (try the "tamper with the schema" break).'
      ]
    },
    templateView: {
      title: "The mandate (template + bound parameters)",
      explainer: [
        "The template is the mandate this run is bound to: the allowed actions, the required steps, and, via the bound max_amount parameter, a $5,000 payment cap.",
        "It is genesis-bound: its hash is committed into the chain's genesis, so every action is bound to this exact template and these exact parameters. The rules cannot be swapped or re-parameterized partway through, and the receipt proves which mandate governed the run. (Proving the mandate was fixed before the work began, in wall-clock time, is the job of the witnessed delegation-manifest layer, not shown here.)",
        "Conformance is checked against exactly these rules, which is why the violation run can be fully valid and yet honestly recorded as non-conformant."
      ]
    },
    receiptView: {
      title: "The sealed receipt (envelope)",
      explainer: [
        "This is the sealed receipt envelope: the top-level document that ties the run together, the chain summary, the ordered action-record hashes, the agent's signatures, and the witness countersignatures.",
        "The raw agent output from the first step is committed here by its hash, so it is tamper-evident: change a single recorded byte anywhere in the package and the chain no longer verifies.",
        "The verifier checks the whole package, not this file alone. It reads this envelope together with actions.jsonl, the evidence files, and (on the template layers) the embedded template and parameters, then recomputes everything in the panel above in your browser."
      ]
    },
    schemaRefusal: {
      title: "Sequesign refused to seal: SchemaValidationError",
      buttonLabel: "Try to record invalid evidence",
      explainer: [
        "In schema_validated mode the SDK validates every evidence blob against its declared schema BEFORE sealing.",
        "Here we take the real payment_instruction_created evidence and set its status to a value the schema does not allow, then run validateEvidenceSchema, the exact check the SDK's schema policy runs at seal time (src/sdk/schema-policy.ts).",
        "Because it fails, the SDK throws SchemaValidationError and never seals. Invalid evidence is rejected at construction, so the receipt is not created in the first place."
      ],
      noReceiptNote:
        "No receipt was produced. This is a construction-time refusal, not a verification failure. There is nothing to verify because nothing was ever sealed."
    },
    // Shared intro shown above the "break it" controls. Accurate for BOTH
    // break kinds: tamper-evidence edits the sealed package; tamper-schema
    // leaves the package intact and substitutes the schema the verifier loads.
    breaksIntro:
      "Each break runs entirely in your browser, then the verifier re-runs and catches it. Some edit the sealed package; the schema break instead swaps a rule the verifier loads. Watch the failing check below."
  },
  breaks: {
    "tamper-evidence": {
      label: "Tamper with evidence",
      hint: "Flips one character in a recorded evidence value after sealing. The committed hash no longer matches, and the verifier rejects the receipt."
    },
    "tamper-schema": {
      label: "Tamper with the schema",
      hint: "Changes the JSON Schema the verifier loads, after the receipt was sealed, leaving the receipt package itself untouched. Because the receipt committed to the schema's hash, the verifier recomputes a different hash, sees the rules were altered, and schema validation fails (schema-hash binding)."
    }
  },
  live: {
    title: "Live run: real seal, real witness, real review",
    intro: [
      "The steps above verify recorded receipts. Live mode produces a brand-new one: pick an agent job, and it runs a real Sequesign session with your own agent key, gets each action countersigned by a real witness, and seals the package (the accounts-payable job also makes one real Claude review call), then verifies it right here in your browser.",
      "The result is verified in EXTERNAL trust-anchor mode: the witness's published keys are fetched from its well-known endpoint (directly, or relayed unchanged through this site when the witness does not allow cross-origin browser reads) and used as the trust anchor, so this is an independent third-party check, not the recorded path's integrity self-check.",
      "Push a job past its mandate, an amount over its cap, and the sealed receipt honestly records conformant: false, produced live. The receipt still verifies as authentic; the violation is recorded, not hidden."
    ],
    contactNote:
      "Live mode contacts the real witness and the real Anthropic API using the keys you enter below. Nothing else on this page makes a network call to those services.",
    keyNote:
      "Keys are used only for this run, in your browser and the request; nothing is stored. They live in React state only, are never written to localStorage, and are cleared when you leave the page.",
    runLabel: "Run live",
    runningLabel: "Running live seal…",
    fields: {
      agentPem: "Agent private key (Ed25519 PEM)",
      agentId: "Agent id",
      witnessBaseUrl: "Witness base URL",
      witnessApiKey: "Witness API key (optional)",
      llmApiKey: "Anthropic API key",
      amountUsd: "Invoice amount (USD)",
      vendor: "Vendor",
      invoiceId: "Invoice id"
    }
  }
};
